import { buildReceiptModel, settlePayments } from '@shripos/shared/billing';
import i18n from '../../i18n/index.js';
import { API_ORIGIN, api, queryClient } from '../api.js';
import { isNetworkError, useOffline } from '../../stores/offline.js';
import { can, useSession } from '../../stores/session.js';
import { cacheCustomer, refreshCustomers } from './cache.js';
import { db, kvSet } from './db.js';
import { forgetHold, markHoldSynced, refreshHolds } from './holds.js';
import { nextOfflineNumbers, offlineCheck, refreshPermit } from './permit.js';

/**
 * Offline billing on a registered device (spec §4.10). A sale completed without internet is
 * numbered from the device's own series, stored in the outbox (IndexedDB) and printed locally.
 * The outbox uploads in order when the server is reachable (bills, open orders held offline and
 * kitchen screen taps); the server de-duplicates, so retries are safe. Refused items stay on the
 * device under "needs attention".
 */

// ---------------------------------------------------------------- build
const discountOf = (d) =>
  d
    ? d.type === 'percent'
      ? { type: 'percent', valueBp: d.valueBp }
      : { type: 'flat', valuePaise: d.valuePaise }
    : null;

/**
 * Complete the cart offline. Returns { bill } shaped like the server's bill (for the receipt
 * and the "paid" dialog) or throws an Error with a user-facing message.
 */
export async function completeOffline({ cart, priced, catalog, payments, shiftId }) {
  const me = useSession.getState().me;
  const check = await offlineCheck();
  if (!check.ok) throw Object.assign(new Error(check.reason), { offlineReason: check.reason });
  const settings = catalog.settings;
  if (cart.couponCode)
    throw new Error(
      i18n.t('offline.coupon', 'Coupons need an internet connection. Remove the coupon to bill offline.'),
    );
  if (payments.some((p) => p.mode === 'points'))
    throw new Error(i18n.t('offline.points', 'Loyalty points need an internet connection.'));
  const settled =
    priced.totals.grandTotalPaise === 0 && !payments.length
      ? { ok: true, payments: [], changePaise: 0, duePaise: 0, paidPaise: 0 }
      : settlePayments(priced.totals.grandTotalPaise, payments);
  if (!settled.ok) throw new Error(settled.error);
  if (settled.duePaise > 0 && !settings.offline.allowCredit)
    throw new Error(i18n.t('offline.credit', 'Credit (due) bills need an internet connection.'));
  if (cart.customer?.offlineNew && !settings.offline.allowNewCustomers)
    throw new Error(i18n.t('offline.newCustomer', 'Adding customers needs an internet connection.'));
  const sent = cart.sentQty ?? {};
  const reduced = Object.entries(sent).some(
    ([lineId, qty]) => qty > (cart.lines.find((l) => l.lineId === lineId)?.qty ?? 0),
  );
  if (reduced && !can(me, 'pos.void'))
    throw new Error(
      i18n.t(
        'offline.removeSent',
        'Removing items already sent to the kitchen needs a manager. Connect to the internet first.',
      ),
    );

  const at = new Date(check.serverNow ?? Date.now());
  // An open order keeps the token it already has.
  const n = await nextOfflineNumbers(check.permit, catalog, at, { token: cart.tokenNo == null });
  const lines = cart.lines
    .map((l) => ({ l, p: priced.byId.get(l.lineId) }))
    .filter((x) => x.p)
    .map(({ l, p }) => ({
      lineId: l.lineId,
      itemId: l.itemId,
      variantId: l.variantId ?? null,
      modifierOptionIds: l.modifierOptionIds ?? [],
      qty: l.qty,
      note: l.note || null,
      discount: discountOf(l.discount),
      unitPricePaise: p.line.unitPricePaise,
      priceMode: p.line.priceMode,
      gstRateBp: p.line.gstRateBp,
      cessRateBp: p.line.cessRateBp ?? 0,
      name: p.meta.name,
      variantName: p.meta.variantName ?? null,
      _meta: p.meta,
      _amounts: p.amounts,
    }));
  const payload = {
    clientUuid: cart.clientUuid,
    capturedAt: at.toISOString(),
    capturedBy: me.user.id,
    shiftId: shiftId ?? null,
    invoiceNo: n.invoiceNo,
    invoiceSeq: n.seq,
    fy: n.fy.code,
    businessDate: n.businessDate,
    tokenNo: cart.tokenNo ?? n.tokenNo,
    orderType: cart.orderType,
    source: cart.source,
    tableNo: cart.tableNo || null,
    customerId: cart.customer?.offlineNew ? null : (cart.customer?.id ?? null),
    newCustomer: cart.customer?.offlineNew ? { phone: cart.customer.phone, name: cart.customer.name || null } : null,
    baseUpdatedAt: cart.baseUpdatedAt ?? null,
    lines: lines.map(({ _meta, _amounts, ...l }) => l),
    billDiscount: cart.billDiscount
      ? { ...discountOf(cart.billDiscount), reason: cart.billDiscount.reason || null }
      : null,
    payments: settled.payments.map(({ mode, amountPaise, tenderedPaise, reference }) => ({
      mode,
      amountPaise,
      ...(tenderedPaise ? { tenderedPaise } : {}),
      ...(reference ? { reference } : {}),
    })),
    notes: cart.notes || null,
    charges: priced.chargeDefs ?? [],
    rounding: settings.rounding,
    totals: { grandTotalPaise: priced.totals.grandTotalPaise, roundOffPaise: priced.totals.roundOffPaise },
  };

  // What the receipt shows now; the synced bill on the server carries the same figures.
  const bill = {
    id: `offline:${cart.clientUuid}`,
    clientUuid: cart.clientUuid,
    status: 'completed',
    invoiceNo: n.invoiceNo,
    invoiceDate: at.toISOString(),
    createdAt: at.toISOString(),
    businessDate: n.businessDate,
    tokenNo: payload.tokenNo,
    orderType: cart.orderType,
    source: cart.source,
    tableNo: payload.tableNo,
    customer: cart.customer
      ? {
          id: cart.customer.id ?? null,
          name: cart.customer.name,
          phone: cart.customer.phone,
          gstin: cart.customer.gstin ?? null,
        }
      : null,
    lines: lines.map((l) => ({
      lineId: l.lineId,
      itemId: l.itemId,
      name: l.name,
      variantName: l.variantName,
      modifiers: l._meta.modifiers ?? [],
      qty: l.qty,
      unitPricePaise: l.unitPricePaise,
      gstRateBp: l.gstRateBp,
      foodType: l._meta.foodType,
      hsnSac: l._meta.hsnSac ?? null,
      note: l.note,
      discount: l.discount,
      amounts: l._amounts,
      kotQty: l.qty,
    })),
    charges: priced.charges ?? [],
    totals: priced.totals,
    taxBreakup: priced.taxBreakup ?? [],
    payments: settled.payments,
    paidPaise: settled.paidPaise,
    changePaise: settled.changePaise,
    duePaise: settled.duePaise,
    completedBy: { id: me.user.id, name: me.user.name },
    snapshot: catalog.receiptSnapshot,
    offline: { capturedAt: at.toISOString(), deviceCode: check.permit.payload.dcode, pending: true },
    loyalty: null,
  };
  const receipt = buildReceiptModel(bill, { upi: catalog.receiptSnapshot?.upi });
  // Same key as an open order of this sale still waiting to sync: the completed bill replaces it.
  await db.outbox.put({
    clientUuid: cart.clientUuid,
    kind: 'bill',
    status: 'pending',
    createdAt: Date.now(),
    attempts: 0,
    payload,
    bill,
    receipt,
    invoiceNo: n.invoiceNo,
    label: n.invoiceNo,
    totalPaise: priced.totals.grandTotalPaise,
  });
  await forgetHold(cart.clientUuid);
  if (payload.newCustomer)
    await cacheCustomer({
      id: `new:${payload.newCustomer.phone}`,
      phone: payload.newCustomer.phone,
      name: payload.newCustomer.name,
      duePaise: 0,
      points: 0,
    });
  await refreshCounts();
  void flushOutbox();
  return { bill, receipt };
}

// ---------------------------------------------------------------- sync
export async function refreshCounts() {
  try {
    const [pending, failed] = await Promise.all([
      db.outbox.where('status').equals('pending').count(),
      db.outbox.where('status').equals('failed').count(),
    ]);
    useOffline.getState().set({ pending, failed });
    return { pending, failed };
  } catch {
    return { pending: 0, failed: 0 };
  }
}

/** Upload one outbox item; it leaves the outbox only once the server has it. */
async function upload(item) {
  if (item.kind === 'hold') {
    const res = await api.post('/sync/holds', item.payload);
    await db.transaction('rw', db.outbox, db.holds, async () => {
      // Saved again offline while uploading: keep the newer save for the next round.
      const latest = await db.outbox.get(item.clientUuid);
      const same = latest?.payload?.syncId === item.payload.syncId;
      if (same) await db.outbox.delete(item.clientUuid);
      if (!latest || same) await markHoldSynced(item.clientUuid, res);
    });
    return;
  }
  if (item.kind === 'kot') {
    await api.post(item.payload.path, item.payload.body);
    await db.outbox.delete(item.clientUuid);
    return;
  }
  await api.post('/sync/bills', item.payload);
  await db.outbox.delete(item.clientUuid);
}

/** Queue a kitchen screen tap made offline (only the latest state of each ticket is sent). */
export async function queueKitchenUpdate(ticketId, path, body) {
  await db.outbox.put({
    clientUuid: `kot:${ticketId}`,
    kind: 'kot',
    status: 'pending',
    createdAt: Date.now(),
    attempts: 0,
    payload: { path, body },
    label: `Kitchen ticket: ${body?.status ?? 'cancellation seen'}`,
    totalPaise: null,
  });
  await refreshCounts();
}

let flushing = null;

/** Upload waiting work, oldest first. Safe to call often; only one run at a time. */
export function flushOutbox() {
  flushing ??= (async () => {
    const st = useOffline.getState();
    st.set({ syncing: true });
    let synced = 0;
    try {
      const items = await db.outbox.where('status').equals('pending').sortBy('createdAt');
      for (const item of items) {
        try {
          await upload(item);
          synced += 1;
          useOffline.getState().set({ reachable: true });
        } catch (err) {
          if (isNetworkError(err) || (err.status ?? 0) >= 500 || err.status === 401) {
            if (isNetworkError(err)) useOffline.getState().set({ reachable: false });
            break; // try again later, in the same order
          }
          // A kitchen tap the server can no longer apply (the ticket moved elsewhere): nothing to fix.
          if (item.kind === 'kot') {
            await db.outbox.delete(item.clientUuid);
            continue;
          }
          await db.outbox.update(item.clientUuid, {
            status: 'failed',
            attempts: (item.attempts ?? 0) + 1,
            error: { code: err.code, message: err.message },
            failedAt: Date.now(),
          });
        }
      }
      if (synced) {
        await kvSet('lastSyncAt', Date.now());
        useOffline.getState().set({ lastSyncAt: Date.now() });
        queryClient.invalidateQueries({ queryKey: ['bills'] });
        queryClient.invalidateQueries({ queryKey: ['kds'] });
        queryClient.invalidateQueries({ queryKey: ['shift-current'] });
        if (can(useSession.getState().me, 'pos.bill'))
          await Promise.all([refreshPermit(), refreshCustomers(), refreshHolds()]);
      }
    } finally {
      await refreshCounts();
      useOffline.getState().set({ syncing: false });
      flushing = null;
    }
    return synced;
  })();
  return flushing;
}

/** Put a "needs attention" bill back in the queue (after fixing the cause, e.g. reopening a shift). */
export async function retryFailed(clientUuid) {
  await db.outbox.update(clientUuid, { status: 'pending', error: null });
  await refreshCounts();
  return flushOutbox();
}

export async function outboxItems() {
  try {
    return await db.outbox.orderBy('createdAt').toArray();
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------- connectivity
/** Is the API reachable? (navigator.onLine is true on Wi-Fi without internet.) */
export async function probe() {
  try {
    const res = await fetch(`${API_ORIGIN}/api/v1/auth/csrf`, { credentials: 'include', cache: 'no-store' });
    const ok = res.ok;
    useOffline.getState().set({ reachable: ok });
    return ok;
  } catch {
    useOffline.getState().set({ reachable: false });
    return false;
  }
}

let timer = null;

/** Start the background loop: probe while unreachable, flush the outbox when reachable. */
export function startOfflineSync() {
  if (timer) return () => {};
  const tick = async () => {
    const { reachable, pending } = useOffline.getState();
    const ok = reachable ? true : await probe();
    if (ok && pending) await flushOutbox();
  };
  const onOnline = () => probe().then((ok) => ok && flushOutbox());
  window.addEventListener('online', onOnline);
  refreshCounts().then(() => tick());
  timer = setInterval(tick, 15_000);
  return () => {
    clearInterval(timer);
    timer = null;
    window.removeEventListener('online', onOnline);
  };
}
